import { useEffect, useState, useCallback } from "react";
import { Trophy, Medal, Radio, Download, FileText, Users, Star } from "lucide-react";
import api, { LOGO } from "@/lib/api";
import { Panel, PageTitle, StarDivider } from "@/components/common";

function RankIcon({ rank }) {
  if (rank === 1) return <Trophy className="w-5 h-5 text-[#D4AF37]" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-[#9CA3AF]" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-[#997A1A]" />;
  return <span className="font-oswald font-bold text-[var(--silver)] w-5 text-center inline-block">{rank}</span>;
}

export default function Classifiche() {
  const [tours, setTours] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const isArchived = (tour) =>
    String(tour.state ?? tour.status ?? "").toLowerCase() === "archived";
  const visibleTours = tours.filter((tour) => showArchived || !isArchived(tour));
  const [tid, setTid] = useState("");
  const [lobbies, setLobbies] = useState([]);
  const [lobbyId, setLobbyId] = useState("");
  const [mode, setMode] = useState("teams"); // teams | mvp
  const [ranking, setRanking] = useState([]);
  const [mvp, setMvp] = useState([]);
  const [live, setLive] = useState(false);

  useEffect(() => { api.get("/tournaments", { params: { templates: false } }).then(({ data }) => { setTours(data);
    const first = data.find((tour) => !isArchived(tour));
    if (first) setTid(first.id); }); }, []);
  useEffect(() => {
    if (!tid) return;
    api.get("/lobbies", { params: { tournament_id: tid } }).then(({ data }) => setLobbies(data));
  }, [tid]);

  const loadRankings = useCallback(() => {
    if (!tid) return;
    const params = {}; if (lobbyId) params.lobby_id = lobbyId;
    api.get(`/tournaments/${tid}/ranking`, { params }).then(({ data }) => setRanking(data));
    api.get(`/tournaments/${tid}/ranking/mvp`, { params }).then(({ data }) => setMvp(data));
  }, [tid, lobbyId]);

  useEffect(() => {
    if (!tid) return;
    loadRankings();
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const q = lobbyId ? `?lobby_id=${lobbyId}` : "";
    let ws;
    try {
      ws = new WebSocket(`${base}/api/ws/ranking/${tid}${q}`);
      ws.onopen = () => setLive(true);
      ws.onmessage = (e) => { try { setRanking(JSON.parse(e.data)); loadRankings(); } catch {} };
      ws.onclose = () => setLive(false);
      ws.onerror = () => setLive(false);
    } catch { setLive(false); }
    return () => { if (ws) ws.close(); };
  }, [tid, lobbyId, loadRankings]);

  const exportRankingPdf = async (kind) => {
    if (!tid) return;
    try {
      const params = { kind };
      if (lobbyId) params.lobby_id = lobbyId;

      const res = await api.get(`/tournaments/${tid}/ranking.pdf`, {
        params,
        responseType: "blob",
      });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `classifica_${kind}_${lobbyId || "generale"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Errore esportazione PDF classifica:", error);
      alert("Impossibile esportare il PDF della classifica.");
    }
  };

  const exportCsv = async () => {
    const res = await api.get(`/tournaments/${tid}/export`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${tid}_risultati.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportPdf = async () => {
    try {
      const res = await api.get(`/tournaments/${tid}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const scopeLabel = lobbyId ? (lobbies.find((l) => l.id === lobbyId)?.name || "Lobby") : "Generale";

  return (
    <div>
      <PageTitle title="Classifiche" subtitle="Squadre & MVP · Lobby & Generale"
        action={<span className={`flex items-center gap-2 font-rajdhani text-xs uppercase tracking-wider ${live ? "text-[#10B981]" : "text-[var(--silver)]"}`} data-testid="live-indicator"><Radio className={`w-4 h-4 ${live ? "animate-pulse" : ""}`} /> {live ? "Live" : "Offline"}</span>} />

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="rank-tour-select" className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
          {visibleTours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
        <label className="flex items-center gap-2 text-sm text-[var(--silver)] font-rajdhani cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowArchived(checked);
              if (!checked && isArchived(tours.find((tour) => tour.id === tid) || {})) {
                setTid(tours.find((tour) => !isArchived(tour))?.id || "");
                setLobbyId("");
              }
            }}
            className="accent-[#D4AF37]"
          />
          Mostra tornei archiviati
        </label>
        <select value={lobbyId} onChange={(e) => setLobbyId(e.target.value)} data-testid="lobby-filter" className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
          <option value="">Generale (tutte le lobby)</option>{lobbies.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        <div className="flex clip-beveled-sm overflow-hidden border border-[var(--steel)]/50">
          <button data-testid="mode-teams" onClick={() => setMode("teams")} className={`px-4 py-2 font-rajdhani text-sm uppercase flex items-center gap-1 ${mode === "teams" ? "bg-[var(--gold)] text-black" : "text-[var(--silver)]"}`}><Users className="w-4 h-4" /> Squadre</button>
          <button data-testid="mode-mvp" onClick={() => setMode("mvp")} className={`px-4 py-2 font-rajdhani text-sm uppercase flex items-center gap-1 ${mode === "mvp" ? "bg-[#a78bfa] text-black" : "text-[var(--silver)]"}`}><Star className="w-4 h-4" /> MVP</button>
        </div>
        <button data-testid="export-csv-btn" onClick={exportCsv} className="btn-gold clip-beveled px-5 py-2 flex items-center gap-2 text-sm"><Download className="w-4 h-4" /> CSV</button>
        <button data-testid="export-teams-pdf-btn" onClick={() => exportRankingPdf("teams")} className="btn-outline-gold clip-beveled px-5 py-2 flex items-center gap-2 text-sm"><FileText className="w-4 h-4" /> PDF Squadre</button>
        <button data-testid="export-mvp-pdf-btn" onClick={() => exportRankingPdf("mvp")} className="btn-outline-gold clip-beveled px-5 py-2 flex items-center gap-2 text-sm"><FileText className="w-4 h-4" /> PDF MVP</button>
        <button data-testid="export-pdf-btn" onClick={exportPdf} className="btn-outline-gold clip-beveled px-5 py-2 flex items-center gap-2 text-sm"><FileText className="w-4 h-4" /> Report PDF</button>
      </div>

      {mode === "teams" ? (
        <Panel gold className="overflow-x-auto" testid="team-leaderboard">
          <div className="bg-[#0a0a0a] p-4 flex items-center justify-center gap-4 border-b border-[var(--gold)]/30">
            <img src={LOGO} alt="" className="w-10 h-10 object-contain" />
            <StarDivider label={`Classifica Squadre · ${scopeLabel}`} className="max-w-md" />
          </div>
          <table className="w-full min-w-[680px]">
            <thead><tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
              <th className="text-left px-4 py-3 w-16">Pos</th><th className="text-left px-4 py-3">Squadra</th>
              <th className="text-right px-4 py-3">Kill</th><th className="text-right px-4 py-3">Vittorie</th>
              <th className="text-right px-4 py-3">Adj</th><th className="text-right px-4 py-3">Punti</th></tr></thead>
            <tbody>
              {ranking.map((r) => (
                <tr key={r.team_id} className={`border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani ${r.rank <= 3 ? "bg-[#1a1a1a] border-l-4 border-[var(--gold)]" : ""}`} data-testid={`rank-row-${r.rank}`}>
                  <td className="px-4 py-3"><RankIcon rank={r.rank} /></td>
                  <td className="px-4 py-3 text-white">{r.name} <span className="text-[var(--gold)] text-xs">({r.tag})</span></td>
                  <td className="px-4 py-3 text-right font-oswald text-white">{r.kills}</td>
                  <td className="px-4 py-3 text-right font-oswald text-white">{r.wins}</td>
                  <td className={`px-4 py-3 text-right font-oswald ${r.adjustment < 0 ? "text-[var(--destructive)]" : r.adjustment > 0 ? "text-[#10B981]" : "text-[var(--silver)]"}`}>{r.adjustment > 0 ? "+" : ""}{r.adjustment || 0}</td>
                  <td className="px-4 py-3 text-right font-oswald font-bold text-2xl text-[var(--gold)]">{r.points}</td>
                </tr>
              ))}
              {ranking.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-[var(--silver)] font-rajdhani">Nessun risultato pubblicato</td></tr>}
            </tbody>
          </table>
        </Panel>
      ) : (
        <Panel className="overflow-x-auto" testid="mvp-leaderboard">
          <div className="bg-[#0a0a0a] p-4 flex items-center justify-center gap-4 border-b border-[#a78bfa]/30">
            <Star className="w-6 h-6 text-[#a78bfa]" />
            <StarDivider label={`Classifica MVP · ${scopeLabel}`} className="max-w-md" />
          </div>
          <table className="w-full min-w-[680px]">
            <thead><tr className="bg-[#0a0a0a] border-b border-[#a78bfa]/40 font-teko text-lg text-[#a78bfa] tracking-wide">
              <th className="text-left px-4 py-3 w-16">Pos</th><th className="text-left px-4 py-3">Giocatore</th>
              <th className="text-left px-4 py-3">Squadra</th><th className="text-right px-4 py-3">Kill</th><th className="text-right px-4 py-3">Punti MVP</th></tr></thead>
            <tbody>
              {mvp.map((r) => (
                <tr key={r.player_id} className={`border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani ${r.rank <= 3 ? "bg-[#1a1a1a] border-l-4 border-[#a78bfa]" : ""}`} data-testid={`mvp-row-${r.rank}`}>
                  <td className="px-4 py-3"><RankIcon rank={r.rank} /></td>
                  <td className="px-4 py-3 text-white">{r.name}</td>
                  <td className="px-4 py-3 text-[var(--silver)]">{r.team_name} <span className="text-[#a78bfa] text-xs">({r.team_tag})</span></td>
                  <td className="px-4 py-3 text-right font-oswald text-white">{r.kills}</td>
                  <td className="px-4 py-3 text-right font-oswald font-bold text-2xl text-[#a78bfa]">{r.points}</td>
                </tr>
              ))}
              {mvp.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-[var(--silver)] font-rajdhani">Nessun dato MVP (servono kill per-giocatore)</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

    </div>
  );
}
